import {
	academicYearStandardFormat,
	getNewGroupReportsAvailableYears as getNewGroupReportsAvailableYearsFromConfig,
} from '../../../../utils/academicYear';
import { getConfig } from 'features/app/redux/features';
import { Report } from '../../types';
import {
	compareAdditionalDownloadsByDate,
	getAdditionalDownloadAcademicYear,
} from './additionalDownloadsHelpers';

/**
 * Redux selectors for accessing report state
 * @module reportSelectors
 */

/**
 * Gets the first personalised target from state
 * @param {Object} state - Redux state
 * @param {Report.State} state.report - Report state
 * @returns {Report.PersonalisedTargets} First personalised target or empty object
 */
export const personalisedTargets = (state: { report: Report.State }) =>
	state.report.personalisedTargets?.[0] ?? {};

/**
 * Gets the currently selected academic year as a formatted option
 * @param {Object} state - Redux state
 * @param {Report.State} state.report - Report state
 * @returns {Report.Option} Selected year as label/value option
 */
export const getSelectedYear = (state: { report: Report.State }): Report.Option => {
	const selectedYear = state.report.selectedYear;
	return {
		label: selectedYear ? (academicYearStandardFormat(selectedYear) as string) : '',
		value: selectedYear ?? '',
	};
};

//THIS IS BECAUSE WE WANT AY 2023 TO BE IN 24/25 ??
/**
 * Gets the currently selected academic year as a formatted option for new group reports
 * @param {Object} state - Redux state
 * @param {Report.State} state.report - Report state
 * @returns {Report.Option} Selected year as label/value option
 */
export const getSelectedYearForNewGroupReports = (state: {
	report: Report.State;
}): Report.Option => {
	const selectedYear = state.report.selectedYearForNewGroupReports;
	return {
		label: selectedYear ? (academicYearStandardFormat(selectedYear + 1) as string) : '',
		value: selectedYear ?? '',
	};
};

/**
 * Gets all available academic years as formatted options
 * @param {Object} state - Redux state
 * @param {Report.State} state.report - Report state
 * @returns {Report.Option[]} Array of year options with labels/values
 */
export const getAvailableYears = (state: { report: Report.State }): Report.Option[] =>
	state.report.availableYears.map((year: number) => ({
		label: academicYearStandardFormat(year) as string,
		value: year,
	}));

/**
 * Gets available academic years for new group reports from Flagsmith config.
 * Newest year first by config — the current year is availableYears[0]?.value.
 */
export const getNewGroupReportsAvailableYears = (state: RootState): Report.Option[] => {
	const config = getConfig('academic_year_end_dates_for_new_group_reports_rollover')(state);
	return getNewGroupReportsAvailableYearsFromConfig(new Date(), config);
};

/**
 * Gets the current academic year for new group reports (newest available year).
 * Available years are sorted newest first by config.
 */
export const getCurrentNewGroupReportsAcademicYear = (state: RootState): number | undefined =>
	getNewGroupReportsAvailableYears(state)[0]?.value as number | undefined;

/**
 * Gets the currently selected academic year for additional downloads
 */
export const getAdditionalSelectedYear = (state: { report: Report.State }): Report.Option => {
	const selectedYear = state.report.additionalSelectedYear;
	return {
		label: selectedYear ? (academicYearStandardFormat(selectedYear) as string) : '',
		value: selectedYear ?? '',
	};
};

/**
 * Gets available academic years for additional downloads, newest first
 */
export const getAdditionalAvailableYears = (state: { report: Report.State }): Report.Option[] =>
	(state.report.additionalAvailableYears ?? [])
		.map((year: number) => ({
			label: academicYearStandardFormat(year) as string,
			value: year,
		}))
		.sort((a, b) => (b.value as number) - (a.value as number));

/**
 * Gets all reports from state
 * @param {Object} state - Redux state
 * @param {Report.State} state.report - Report state
 * @returns {Report.Report[]} Array of all reports
 */
export const getMyReports = (state: { report: Report.State }) => state.report.myReports;

/**
 * Gets reports for the selected academic year, sorted by name
 * @param {Object} state - Redux state
 * @param {Report.State} state.report - Report state
 * @returns {Report.Report[]} Filtered and sorted array of reports
 */
export const getMyReportsForSelectedYear = (state: { report: Report.State }) =>
	state.report.myReports
		.filter((x: Report.Report) => x.AcademicYear === state.report.selectedYear)
		.sort((a: Report.Report, b: Report.Report) => {
			if (a.Name < b.Name) {
				return -1;
			}
			if (a.Name > b.Name) {
				return 1;
			}
			return 0;
		});

/**
 * Gets report data for a specific report ID
 * @param {number} id - Report ID
 * @returns {Function} Selector function that takes state and returns report data
 */
export const getReportData = (id: number) => (state: { report: Report.State }) =>
	state.report.reportData[id];

/**
 * Gets additional report data from state
 * @param {Object} state - Redux state
 * @param {Report.State} state.report - Report state
 * @returns {Report.Additional[]} Array of additional report data
 */
export const getAdditional = (state: { report: Report.State }) => state.report.additional;

/**
 * Gets additional downloads for the selected academic year
 * @param {Object} state - Redux state
 * @param {Report.State} state.report - Report state
 * @returns {Report.Additional[]} Array of additional downloads for the selected year
 */
export const getAdditionalForSelectedYear = (state: { report: Report.State }) =>
	state.report.additional
		.filter(
			(download: Report.Additional) =>
				getAdditionalDownloadAcademicYear(download) === state.report.additionalSelectedYear
		)
		.sort(compareAdditionalDownloadsByDate);

/**
 * Gets additional data for a specific report ID
 * @param {number} id - Report ID
 * @returns {Function} Selector function that takes state and returns additional data
 */
export const getAdditionalData = (id: number) => (state: { report: Report.State }) =>
	state.report.additionalData[id];

/**
 * Gets the loading state
 * @param {Object} state - Redux state
 * @param {Report.State} state.report - Report state
 * @returns {boolean} Whether data is currently loading
 */
export const isLoading = (state: { report: Report.State }) => state.report.loading;

/**
 * Gets the loading state for my reports
 * @param {Object} state - Redux state
 * @param {Report.State} state.report - Report state
 * @returns {boolean} Whether my reports are currently loading
 */
export const isLoadingMyReports = (state: { report: Report.State }) =>
	state.report.loadingMyReports;
